import { PackEditorTabMode } from 'enums';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getHabitPackById,
  getUserInstalledHabitPacks,
  installHabit
} from './extra';
import { initialHabitPackState } from 'store/initial-states';
import { FocusModeTemplateType, HabitPackType } from 'interfaces/common.interface';
import {
  PackAndFocusEditorMode,
  PackAndFocusEditorType,
  PackPreviewType,
  PackPreviewVariant,
  UserSettingsSnapshot
} from 'interfaces/initial-states.interface';
import { SettingsType } from 'interfaces/setting.interface';

const habitPack = createSlice({
  name: 'habit-pack',
  initialState: initialHabitPackState,
  reducers: {
    updateAreHabitPacksLoading: (state, action: PayloadAction<boolean>) => {
      state.areHabitPacksLoading = action.payload;
    },
    setMarketplaceApprovedPacks: (
      state,
      action: PayloadAction<HabitPackType[]>
    ) => {
      state.marketplaceApprovedPacks = action.payload;
    },
    setUserInstalledHabitPacks: (
      state,
      action: PayloadAction<HabitPackType[]>
    ) => {
      state.installedHabitPacks = action.payload;
    },
    setUserCreatedHabitPacks: (
      state,
      action: PayloadAction<HabitPackType[]>
    ) => {
      state.userCreatedHabitPacks = action.payload;
    },
    setFeaturedHabitPacks: (state, action: PayloadAction<HabitPackType[]>) => {
      state.featuredHabitPacks = action.payload;
    },
    setIsOnHabitPacksTab: (state, { payload }: PayloadAction<boolean>) => {
      state.isOnHabitPacksTab = payload;
    },
    setCurrentViewedPack: (
      state,
      { payload }: PayloadAction<HabitPackType | null>
    ) => {
      state.currentViewedPack = payload;
    },
    updateIsCurrentHabitPackFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCurrentHabitPackFetching = payload;
    },
    updateIsSharedHabitPackFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSharedHabitPackFetching = payload;
    },
    updateSharedViewedPack: (
      state,
      { payload }: PayloadAction<HabitPackType>
    ) => {
      state.sharedViewedPack = payload;
    },
    updateIsUserInstalledPacksFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUserInstalledPacksFetching = payload;
    },
    updateInstallingPack: (state, { payload }: PayloadAction<boolean>) => {
      state.installingPack = payload;
    },
    updateUninstallingPack: (state, { payload }: PayloadAction<boolean>) => {
      state.uninstallingPack = payload;
    },
    updateIsUserUpsertingPack: (state, { payload }: PayloadAction<boolean>) => {
      state.isUserUpsertingPack = payload;
    },
    updateIsHabitPackAllowedToBeEdited: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isHabitPackAllowedToBeEdited = payload;
    },
    updateIsHabitPackPdfShowed: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isHabitPackPdfShowed = payload;
    },
    updateInitialCurrentViewedPack: (
      state,
      { payload }: PayloadAction<SettingsType | null>
    ) => {
      state.initialCurrentViewedPack = payload;
    },
    updateIsHabitPacksDeleting: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isHabitPacksDeleting = payload;
    },
    updateOnboardingHabitPacks: (
      state,
      { payload }: PayloadAction<HabitPackType[]>
    ) => {
      state.onboardingHabitPacks = payload;
    },
    updateIsSendingInstalledHabitPackId: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSendingInstalledHabitPackId = payload;
    },
    updateIsSendingInstalledHabitId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.isSendingInstalledHabitId = action.payload;
    },
    openPackAndFocusEditor: (
      state,
      {
        payload
      }: PayloadAction<{
        editorType: PackAndFocusEditorType;
        editorMode: PackAndFocusEditorMode;
        packEditorTabMode: PackEditorTabMode;
        userSettingsSnapshot: UserSettingsSnapshot;
        focusTemplateSnapshot?: FocusModeTemplateType | null;
        initialFocusTemplateSnapshot?: FocusModeTemplateType | null;
      }>
    ) => {
      state.isPackAndFocusEditorOpen = true;
      state.editorType = payload.editorType;
      state.editorMode = payload.editorMode;
      state.packEditorTabMode = payload.packEditorTabMode;
      state.userSettingsSnapshot = payload.userSettingsSnapshot;
      state.focusTemplateSnapshot = payload.focusTemplateSnapshot ?? null;
      state.initialFocusTemplateSnapshot =
        payload.initialFocusTemplateSnapshot ?? null;
    },
    closePackAndFocusEditor: (state) => {
      state.isPackAndFocusEditorOpen = false;
      state.packEditorTabMode = null;
      state.editorType = null;
      state.editorMode = null;
      state.userSettingsSnapshot = null;
      state.focusTemplateSnapshot = null;
      state.initialFocusTemplateSnapshot = null;
    },
    openPackPreview: (
      state,
      {
        payload
      }: PayloadAction<{
        previewType: PackPreviewType;
        previewEntityId: string;
        previewVariant: PackPreviewVariant;
      }>
    ) => {
      state.isPackPreviewOpen = true;
      state.previewType = payload.previewType;
      state.previewEntityId = payload.previewEntityId;
      state.previewVariant = payload.previewVariant;
    },
    closePackPreview: (state) => {
      state.isPackPreviewOpen = false;
      state.previewType = null;
      state.previewEntityId = null;
      state.previewVariant = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(installHabit.pending, (state, { meta }) => {
        state.isSendingInstalledHabitId = meta.arg; // arg = activityId
      })
      .addCase(installHabit.fulfilled, (state) => {
        state.isSendingInstalledHabitId = null;
      })
      .addCase(installHabit.rejected, (state) => {
        state.isSendingInstalledHabitId = null;
      })
      .addCase(getUserInstalledHabitPacks.fulfilled, (state, action) => {
        state.installedHabitPacks = action.payload ?? [];
        state.isUserInstalledPacksFetching = false;
      })
      .addCase(getUserInstalledHabitPacks.rejected, (state) => {
        state.isUserInstalledPacksFetching = false;
      })
      .addCase(getHabitPackById.fulfilled, (state, { payload }) => {
        state.currentViewedPack = payload;
        state.isCurrentHabitPackFetching = false;
      })
      .addCase(getHabitPackById.rejected, (state) => {
        state.isCurrentHabitPackFetching = false;
      });
  }
});

export const {
  setUserInstalledHabitPacks,
  setFeaturedHabitPacks,
  setMarketplaceApprovedPacks,
  updateAreHabitPacksLoading,
  setIsOnHabitPacksTab,
  setUserCreatedHabitPacks,
  setCurrentViewedPack,
  updateIsCurrentHabitPackFetching,
  updateIsSharedHabitPackFetching,
  updateSharedViewedPack,
  updateIsUserInstalledPacksFetching,
  updateInstallingPack,
  updateUninstallingPack,
  updateIsUserUpsertingPack,
  updateIsHabitPackAllowedToBeEdited,
  updateIsHabitPackPdfShowed,
  updateInitialCurrentViewedPack,
  updateIsHabitPacksDeleting,
  updateOnboardingHabitPacks,
  updateIsSendingInstalledHabitPackId,
  updateIsSendingInstalledHabitId,
  openPackAndFocusEditor,
  closePackAndFocusEditor,
  openPackPreview,
  closePackPreview
} = habitPack.actions;

export default habitPack.reducer;
